"use client";

import { useState } from "react";
import Link from "next/link";
import { Mail, MessageCircle, Users, Share2, Clock, CheckCircle } from "lucide-react";
import { DISCORD_COMMUNITY_HREF, WHATSAPP_CHANNEL_HREF } from "@/lib/community";

const FORMSUBMIT_ENDPOINT = "https://formsubmit.co/clearcurve1@gmail.com";

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(FORMSUBMIT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          _subject: `ClearCurve contact: ${formData.subject}`,
          _captcha: "false",
        }),
      });
      if (!res.ok) throw new Error("send_failed");
      setSubmitted(true);
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch {
      setError("Message could not be sent. Please email us directly at clearcurve1@gmail.com.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* CONTACT HEADER */}
      <section className="page-header page-header--accent">
        <div className="page-header-container">
          <h1 className="page-title">Get In Touch</h1>
          <p className="page-subtitle">Have questions? We&apos;d love to hear from you</p>
        </div>
      </section>

      {/* CONTACT CONTENT */}
      <section className="contact-section">
        <div className="contact-container">
          <div className="contact-grid">
            {/* CONTACT INFO */}
            <div className="contact-info">
              <h2>Contact Information</h2>
              
              <div className="contact-item">
                <div className="contact-icon" aria-hidden="true"><Mail size={28} strokeWidth={1.6} color="var(--color-primary)" /></div>
                <div>
                  <h3>Email</h3>
                  <p>
                    <a href="mailto:clearcurve1@gmail.com">clearcurve1@gmail.com</a>
                  </p>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon" aria-hidden="true"><MessageCircle size={28} strokeWidth={1.6} color="var(--color-primary)" /></div>
                <div>
                  <h3>WhatsApp Channel</h3>
                  <p>
                    <a href={WHATSAPP_CHANNEL_HREF} target="_blank" rel="noopener noreferrer">
                      Follow the ClearCurve PSX channel on WhatsApp
                    </a>
                  </p>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon" aria-hidden="true"><Users size={28} strokeWidth={1.6} color="var(--color-primary)" /></div>
                <div>
                  <h3>Discord Community</h3>
                  <p>
                    <a href={DISCORD_COMMUNITY_HREF} target="_blank" rel="noopener noreferrer">Join our Discord community</a> to discuss strategies and learn from other traders
                  </p>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon" aria-hidden="true"><Share2 size={28} strokeWidth={1.6} color="var(--color-primary)" /></div>
                <div>
                  <h3>Social Media</h3>
                  <p>
                    Follow us on{" "}
                    <a href={WHATSAPP_CHANNEL_HREF} target="_blank" rel="noopener noreferrer">WhatsApp</a>{" "}
                    and <a href={DISCORD_COMMUNITY_HREF} target="_blank" rel="noopener noreferrer">Discord</a> for updates
                  </p>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon" aria-hidden="true"><Clock size={28} strokeWidth={1.6} color="var(--color-primary)" /></div>
                <div>
                  <h3>Response Time</h3>
                  <p>We typically respond within 24-48 hours during business days</p>
                </div>
              </div>
            </div>

            {/* CONTACT FORM */}
            <div className="contact-form-wrapper">
              <h2>Send us a Message</h2>
              
              {submitted ? (
                <div className="form-success">
                  <div className="success-icon" aria-hidden="true">
                    <CheckCircle size={44} strokeWidth={1.5} color="var(--color-success)" />
                  </div>
                  <h3>Message Sent!</h3>
                  <p>Thank you for your message. We&apos;ll get back to you soon.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form" aria-label="Contact form">
                  <p className="contact-form-required-note"><span aria-hidden="true">*</span> Required fields</p>
                  <div className="form-group">
                    <label htmlFor="name">Name <span className="form-required" aria-hidden="true">*</span></label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      autoComplete="name"
                      placeholder="Your name"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email <span className="form-required" aria-hidden="true">*</span></label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      autoComplete="email"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="subject">Subject <span className="form-required" aria-hidden="true">*</span></label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      placeholder="What is this about?"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Message <span className="form-required" aria-hidden="true">*</span></label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      placeholder="Your message..."
                    />
                  </div>

                  {error ? (
                    <p role="alert" style={{ color: "var(--color-danger-vivid)", marginBottom: "0.75rem", fontSize: "0.9rem" }}>
                      {error}
                    </p>
                  ) : null}
                  <button type="submit" className="btn btn-primary" disabled={submitting} aria-busy={submitting}>
                    {submitting ? "Sending…" : "Send Message"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="faq-section">
        <div className="faq-container">
          <h2>Frequently Asked Questions</h2>
          
          <div className="faq-grid">
            <div className="faq-item">
              <h3>Is ClearCurve free?</h3>
              <p>Yes, ClearCurve is completely free to use. No subscriptions or hidden fees.</p>
            </div>

            <div className="faq-item">
              <h3>Can I use this data for trading?</h3>
              <p>
                The data is for educational purposes. Always verify with official sources before making trading decisions. 
                We recommend paper trading first.
              </p>
            </div>

            <div className="faq-item">
              <h3>How often is data updated?</h3>
              <p>Market data is updated daily with the latest closing prices and technical indicators.</p>
            </div>

            <div className="faq-item">
              <h3>Can I download the data?</h3>
              <p>Currently, data can be viewed on the platform. CSV export feature is coming soon.</p>
            </div>

            <div className="faq-item">
              <h3>How do I learn a specific strategy?</h3>
              <p>Visit the &quot;Learn Before You Scan&quot; section to find video tutorials for each strategy.</p>
            </div>

            <div className="faq-item">
              <h3>Is there a disclaimer I should read?</h3>
              <p>
                Yes! Please visit our <Link href="/about">About page</Link> and
                <Link href="/privacy-policy"> Privacy Policy</Link> to understand our legal disclaimers.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
